/**
 * Scene Description API — generates visual prompts from conversation for owl-themed scenes
 * When the user has an avatar, they appear in the scene WITH the owl (like FTW).
 */

import { callLLM } from '../utils/llm';
import type { OwlId } from '../types/owl';

/** Precise owl description — MUST match the hard-surface geometric low-poly brand mascot */
const OWL_CHARACTER = 'a geometric low-poly origami-style owl character made of flat triangular polygon facets with sharp angular edges (NOT smooth, NOT organic). Steel-blue periwinkle faceted body, soft pink triangular V-shaped chest, very large dark glossy sphere eyes with light gray rings and tiny cyan dot highlights, small pink diamond beak, small pink polygon feet. Compact chunky ball-shaped body with pointed angular ear tufts. Hard-surface faceted 3D render like papercraft';

/** Body proportions for the human avatar — keeps people slim instead of chunky/chibi */
export const PERSON_PROPORTIONS = 'confident posture and the same face, body, hair, and look as their chosen avatar (realistic adult proportions, NOT chibi or big-headed)';

/**
 * Owl workshop scenes for early conversation / no specific topic yet.
 * Default (no client profile) scenes feature Meaningful AI's home city, Atlanta.
 */
const ATLANTA_WORKSHOP_SCENES = [
    `${OWL_CHARACTER} floating happily over the beautiful Atlanta skyline and Beltline on a sunny day. Clean, natural, and grounded workspace view. No glowing holograms.`,
    `${OWL_CHARACTER} flying over the Atlanta skyline at sunset, with warm golden light reflecting off the skyscrapers. A peaceful perspective of the city. No floating UI elements.`,
    `${OWL_CHARACTER} hovering above the Atlanta Beltline surrounded by green trees and city lights. No glowing screens.`,
    `${OWL_CHARACTER} perched in a cozy treehouse with a view of the Atlanta skyline, warm amber lamplight, and shelves of physical books. No glowing holograms.`,
    `${OWL_CHARACTER} hovering proudly near the Centennial Olympic Park fountain, with the Atlanta skyline glittering behind. No futuristic interfaces.`,
    `${OWL_CHARACTER} exploring a beautiful atrium with views of the Atlanta city center, sunlight streaming through a glass ceiling. A grounded environment without futuristic interfaces.`,
];

/** Same scenes for a client's city. `place` is e.g. "the Denver, Colorado skyline" or "a modern city skyline". */
function clientWorkshopScenes(place: string, city: string | null): string[] {
    const cityLabel = city ? ` in ${city}` : '';
    return [
        `${OWL_CHARACTER} floating happily over ${place} on a sunny day. Clean, natural, and grounded workspace view. No glowing holograms.`,
        `${OWL_CHARACTER} flying over ${place} at sunset, with warm golden light reflecting off the buildings. A peaceful perspective of the city. No floating UI elements.`,
        `${OWL_CHARACTER} hovering above a green urban park${cityLabel}, with ${place} behind and trees all around. No glowing screens.`,
        `${OWL_CHARACTER} perched in a cozy treehouse with a view of ${place}, warm amber lamplight, and shelves of physical books. No glowing holograms.`,
        `${OWL_CHARACTER} hovering proudly near a well-known downtown landmark${cityLabel}, with ${place} glittering behind. No futuristic interfaces.`,
        `${OWL_CHARACTER} exploring a beautiful atrium with views of ${place}, sunlight streaming through a glass ceiling. A grounded environment without futuristic interfaces.`,
    ];
}

/**
 * clientCity: undefined = no client profile (use Atlanta);
 * null = client profile without a city (generic skyline, never Atlanta);
 * string = the client's city.
 */
function sceneSetting(clientCity: string | null | undefined) {
    if (clientCity === undefined) {
        return {
            scenes: ATLANTA_WORKSHOP_SCENES,
            rule: '- The setting should ideally incorporate views of the Atlanta skyline or Beltline where appropriate',
        };
    }
    if (clientCity) {
        return {
            scenes: clientWorkshopScenes(`the ${clientCity} skyline`, clientCity),
            rule: `- The setting should ideally incorporate recognizable views of ${clientCity} (its skyline or well-known landmarks) where appropriate. Do NOT show Atlanta.`,
        };
    }
    return {
        scenes: clientWorkshopScenes('a modern city skyline', null),
        rule: '- If a city appears, use a generic modern city skyline. Do NOT show Atlanta or any specific named city.',
    };
}

/** Rotates through the client's settings so consecutive scenes show different places */
let clientSettingTurn = Math.floor(Math.random() * 100);

/** Client work environments, derived once per profile and reused for every scene */
const clientSettingsCache = new Map<string, Promise<string[]>>();

/**
 * Turn a client profile into a short list of concrete, visual places where that company's
 * people actually work (job sites, rail yards, plants…), so scenes reflect the business
 * instead of defaulting to a generic office.
 */
function getClientSettings(clientContext: string, provider?: string): Promise<string[]> {
    const key = clientContext.slice(0, 2000);
    const cached = clientSettingsCache.get(key);
    if (cached) return cached;

    const prompt = `Read this company profile and list 6 specific, visual, real-world places where this company's people actually do their work, for a 3D animated illustrator.
Favor field and operational settings that show what the company physically does (e.g. "a busy road construction site with excavators and fresh concrete curb and gutter", "a rail yard with crews replacing ties beside a locomotive"). Include at most one office-type setting, only if office work is central to the company. Describe each place in one vivid phrase with concrete props, no people, no brand names or logos.
Respond ONLY with a JSON array of 6 strings.

COMPANY PROFILE:
${clientContext.slice(0, 4000)}`;

    const promise = callLLM({ prompt, temperature: 0.4, maxTokens: 500, jsonMode: true, provider })
        .then(res => {
            if (!res.success) return [];
            const match = res.content.match(/\[[\s\S]*\]/);
            const list = match ? JSON.parse(match[0]) : [];
            return Array.isArray(list) ? list.filter((s: unknown) => typeof s === 'string' && s.trim()).slice(0, 8) : [];
        })
        .catch(() => [] as string[]);
    clientSettingsCache.set(key, promise);
    promise.then(list => { if (!list.length) clientSettingsCache.delete(key); });
    return promise;
}

function hasSpecificTopic(history: { role: string; content: string }[]): boolean {
    if (history.length <= 2) return false;
    const recentUserMessages = history
        .filter(h => h.role === 'user')
        .slice(-3)
        .map(h => h.content.toLowerCase())
        .join(' ');
    if (recentUserMessages.length < 40) return false;
    return true;
}

/**
 * Extract a visual scene description from recent conversation context
 */
export async function extractSceneDescription(
    playerName: string,
    playerDescription: string,
    recentHistory: { role: string; content: string; agent_id?: string }[],
    activeOwlId: OwlId,
    owlOnly: boolean = false,
    provider?: string,
    clientCity?: string | null,
    clientContext?: string
): Promise<string> {
    const stylePrefix = 'Warm cinematic 3D rendered scene with soft volumetric lighting, high quality textures.';
    const setting = sceneSetting(clientCity);
    const clientSettings = clientContext ? await getClientSettings(clientContext, provider) : [];
    const pickClientSetting = () => clientSettings[clientSettingTurn++ % clientSettings.length];
    const inCity = clientCity ? ` in ${clientCity}` : '';
    const pickScene = () => clientSettings.length
        ? `${OWL_CHARACTER} hovering happily at ${pickClientSetting()}${inCity}. Grounded, realistic environment. No glowing holograms or floating UI.`
        : setting.scenes[Math.floor(Math.random() * setting.scenes.length)];

    // Early conversation — use preset workshop scenes (or the client's own work environments)
    if (!hasSpecificTopic(recentHistory)) {
        const scene = pickScene();
        if (owlOnly) {
            return `${stylePrefix} ${scene}`;
        } else {
            return `${stylePrefix} ${scene} Nearby is ${playerName} — ${playerDescription}, with a ${PERSON_PROPORTIONS}. Keep ${playerName}'s gender, face, hair, and glasses exactly as in their reference image. They are having a warm conversation together.`;
        }
    }

    // Topic-driven — generate scene matching the discussion
    const historySnippet = recentHistory.slice(-6).map(h =>
        `${h.role === 'user' ? playerName : (h.agent_id || 'Owl')}: ${h.content}`
    ).join('\n');

    const characterBlock = owlOnly
        ? `THE CHARACTER: ${OWL_CHARACTER}. CRITICAL: The owl MUST be geometric low-poly with flat faceted surfaces like origami/papercraft. NOT smooth or organic looking.`
        : `THE CHARACTERS:
1. ${OWL_CHARACTER}. CRITICAL: The owl MUST be geometric low-poly with flat faceted surfaces like origami/papercraft. NOT smooth.
2. ${playerName} — ${playerDescription}, with a ${PERSON_PROPORTIONS}. They are having a conversation together.
CRITICAL: Describe ${playerName} exactly as given above: same gender, hair, skin tone, and glasses. Never change their appearance or gender.`;

    const clientPlace = clientSettings.length ? pickClientSetting() : '';
    const clientWorld = clientPlace
        ? `\nTHE SETTING — a place where this company's people really work: ${clientPlace}${inCity}\n`
        : '';
    const placeRule = clientPlace
        ? `- Set the scene exactly in THE SETTING above, and bring the topic in through props there. For example, a conversation about proposals or reports becomes plans and a tablet on a tailgate at a job site, not a conference room.
- Do NOT use a generic corporate office, cubicles, or conference room.`
        : `- The environment MUST match the conversation subject`;

    const prompt = `You generate scene descriptions for a 3D animated image generator. Read the conversation and create a scene that VISUALLY MATCHES the topic and the client's business.

CONVERSATION:
${historySnippet}
${clientWorld}
${characterBlock}

YOUR TASK: Describe a scene where the character(s) are IN an environment that relates to the client's business and the conversation topic. The setting should make both visually obvious.

CRITICAL RULES:
${placeRule}
- Include SPECIFIC physical visual props related to the topic
${setting.rule}
- The characters should be actively engaged with the environment (e.g. Poly floating/flying happily around)
- Warm cinematic lighting, 3D rendered, high quality textures
- DO NOT INCLUDE floating UI, glowing holograms, or futuristic sci-fi interfaces. Keep the scene grounded and realistic.
- Output ONLY the scene description in 2-3 sentences. No dialogue.`;

    try {
        const response = await callLLM({ prompt, temperature: 0.7, maxTokens: 250, provider });
        if (response.success) {
            return `${stylePrefix} ${response.content}`;
        }
    } catch (error) {
        console.error('[Scene] Failed to extract scene description:', error);
    }

    // Fallback
    return `${stylePrefix} ${pickScene()}`;
}
